import { useQuery } from 'react-query';
import { useEffect, useRef } from 'react';

import LoadingWrapper from 'src/components/Loading/LoadingWrapper';
import useScrollToTop from 'src/hooks/useScrollToTop';
import SessionService from 'src/services/SessionService';
import useSessionStore from 'src/stores/session';
import useUserStore from 'src/stores/user';
import { notifyPurchaseIdentityAuthenticated } from 'src/services/RegistrationAnalytics';

interface IAuthProviderProps {
    children: React.ReactNode;
}

export default function AuthProvider({ children }: IAuthProviderProps) {
    useScrollToTop();
    const hasSessionTokens = Boolean(SessionService.accessToken || SessionService.refreshToken);
    const isRefreshingRef = useRef(false);
    const userId = useUserStore((state) => state.id);

    const { getUserSessionRequiredData, isAuthorized, checkUserDataChanges } = useSessionStore(
        ({ isAuthorized, getUserSessionRequiredData, checkUserDataChanges }) => ({
            isAuthorized,
            getUserSessionRequiredData,
            checkUserDataChanges,
        })
    );

    const { isLoading: isUserLoading } = useQuery('user', getUserSessionRequiredData, {
        enabled: hasSessionTokens,
        refetchOnMount: false, // Используем кеш, чтобы не делать повторные запросы
        staleTime: 30000, // Данные считаются свежими 30 секунд
        cacheTime: 60000, // Кеш хранится 60 секунд
    });

    useEffect(() => {
        notifyPurchaseIdentityAuthenticated(isAuthorized ? userId : null);
    }, [isAuthorized, userId]);

    // Умное обновление данных пользователя
    useEffect(() => {
        if (!isAuthorized) {
            return;
        }

        const interval = setInterval(async () => {
            // Проверяем только если вкладка активна
            if (document.hidden || isRefreshingRef.current) {
                return;
            }

            try {
                isRefreshingRef.current = true;
                await checkUserDataChanges();
            } finally {
                isRefreshingRef.current = false;
            }
        }, 15000); // Проверяем каждые 15 секунд

        // Проверяем изменения при возвращении фокуса на вкладку
        const handleVisibilityChange = async () => {
            if (document.hidden || isRefreshingRef.current) {
                return;
            }

            try {
                isRefreshingRef.current = true;
                await checkUserDataChanges();
            } finally {
                isRefreshingRef.current = false;
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [isAuthorized, checkUserDataChanges]);

    return (
        <LoadingWrapper
            isLoading={isUserLoading}
            isForced={!isAuthorized && hasSessionTokens}
        >
            {children}
        </LoadingWrapper>
    );
}
